import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutDashboard, Route as RouteIcon, Map, History, User, Star, Plane, MapPin, ArrowRight, Phone, MessageCircle, ChevronRight } from "lucide-react";
import PanelShell from "../../components/PanelShell";
import StatusBadge from "../../components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { driverApi } from "@/lib/driverApi";

const navItems = [
  { to: "/conductor", label: "Inicio", icon: LayoutDashboard, end: true },
  { to: "/conductor/ruta", label: "Ruta optimizada", icon: RouteIcon },
  { to: "/conductor/rutas-tarifas", label: "Mis rutas", icon: Map },
  { to: "/conductor/historial", label: "Historial", icon: History },
  { to: "/conductor/calificaciones", label: "Calificaciones", icon: Star },
  { to: "/conductor/perfil", label: "Perfil", icon: User },
];
const bottomNav = [
  { to: "/conductor", label: "Inicio", icon: LayoutDashboard, end: true },
  { to: "/conductor/ruta", label: "Ruta", icon: RouteIcon },
  { to: "/conductor/rutas-tarifas", label: "Rutas", icon: Map },
  { to: "/conductor/historial", label: "Viajes", icon: History },
  { to: "/conductor/perfil", label: "Perfil", icon: User },
];

const STATUS_LABELS = { pendiente_pago: "Pendiente", confirmado: "Confirmado", completado: "Completado", cancelado: "Cancelado" };

// Perú usa UTC-5 fijo todo el año (sin horario de verano), mismo criterio
// que backend/app/core/timezones.py. Se usa este offset fijo -- no la hora
// local del navegador, ni una zona con reglas de DST -- para que "hoy" y las
// horas mostradas sean consistentes con el resto del sistema sin importar
// dónde esté el dispositivo del conductor.
const LIMA_OFFSET_MS = -5 * 60 * 60 * 1000;

// Devuelve un Date cuyos getters UTC reflejan la hora de pared en Lima del
// instante dado (getUTCHours/getUTCDate/... leen como hora de Lima).
const toLima = (date) => new Date(date.getTime() + LIMA_OFFSET_MS);

const greeting = (limaNow) => {
  const h = limaNow.getUTCHours();
  if (h < 12) return "Buenos días";
  if (h < 19) return "Buenas tardes";
  return "Buenas noches";
};

const isSameLimaDay = (isoString, limaNow) => {
  const d = toLima(new Date(isoString));
  return (
    d.getUTCFullYear() === limaNow.getUTCFullYear() &&
    d.getUTCMonth() === limaNow.getUTCMonth() &&
    d.getUTCDate() === limaNow.getUTCDate()
  );
};

const timeLabel = (isoString) => {
  const d = toLima(new Date(isoString));
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
};

const DriverDashboard = () => {
  const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await driverApi.listReservations();
        setReservations(data);
      } catch {
        setError("No se pudieron cargar tus viajes.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const { todayTrips, nextStop, stats } = useMemo(() => {
    const now = new Date();
    const limaNow = toLima(now);
    const active = reservations.filter((r) => r.status !== "cancelado");
    const today = active
      .filter((r) => isSameLimaDay(r.scheduled_at, limaNow))
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));

    // Próxima parada: el viaje futuro más cercano que no esté completado ni
    // cancelado (mira todos los viajes, no solo los de hoy).
    const upcoming = active
      .filter((r) => r.status !== "completado" && new Date(r.scheduled_at) >= now)
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));

    const todayNotCancelled = today;
    const revenueToday = todayNotCancelled.reduce((acc, r) => acc + Number(r.price_total), 0);

    return {
      todayTrips: today,
      nextStop: upcoming[0] || null,
      stats: {
        tripsToday: todayNotCancelled.length,
        confirmedToday: todayNotCancelled.filter((r) => r.status === "confirmado").length,
        unpaidToday: todayNotCancelled.filter((r) => r.payment_status !== "pagado").length,
        revenueToday,
      },
    };
  }, [reservations]);

  const phoneDigits = (phone) => (phone || "").replace(/[^\d]/g, "");
  // Hora de Lima para el saludo y la fecha del encabezado (ver toLima).
  const limaNow = toLima(new Date());

  return (
    <PanelShell role="Conductor" navItems={navItems} bottomNavItems={bottomNav} userName={user?.full_name} testId="driver-dashboard">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display">
              Turno · {limaNow.toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" })}
            </p>
            <h1 className="font-display font-bold text-3xl uppercase tracking-tight mt-1">
              {greeting(limaNow)}{user?.full_name ? `, ${user.full_name.split(" ")[0]}` : ""}
            </h1>
            <p className="mt-2 text-sm text-[#A0A0A5] font-body">
              {loading ? "Cargando tus viajes…" : `Tienes ${stats.tripsToday} ${stats.tripsToday === 1 ? "viaje" : "viajes"} para hoy.`}
            </p>
          </div>
          <Link to="/conductor/ruta" className="inline-flex items-center gap-2 px-5 py-3 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] font-display font-bold uppercase tracking-wider text-xs rounded-lg" data-testid="view-route-btn">
            Ver ruta optimizada <ArrowRight size={14} />
          </Link>
        </div>

        {error && <p className="mb-4 text-xs text-red-400 font-body" data-testid="driver-dashboard-error">{error}</p>}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MiniStat label="Viajes hoy" value={stats.tripsToday} unit="agendados" />
          <MiniStat label="Confirmados" value={stats.confirmedToday} unit="de hoy" />
          <MiniStat label="Pago pendiente" value={stats.unpaidToday} unit="de hoy" />
          <MiniStat label="Ingresos de hoy" value={`S/ ${stats.revenueToday.toFixed(2)}`} unit="no cancelados" gold />
        </div>

        {/* Next stop */}
        {nextStop && (
          <div className="mt-6 relative overflow-hidden bg-gradient-to-br from-[#1C1D20] to-[#0D0E10] border border-[#C9A227]/40 rounded-xl p-6 lg:p-8" data-testid="current-trip">
            <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#C9A227]/10 blur-3xl" />
            <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3">
                  <StatusBadge status={STATUS_LABELS[nextStop.status]} />
                  <span className="text-[10px] uppercase tracking-brand text-[#C9A227] font-display flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A227]" /> Próxima parada · {timeLabel(nextStop.scheduled_at)}
                  </span>
                </div>
                <p className="font-display font-bold text-2xl lg:text-3xl">{nextStop.contact_name}</p>
                <p className="text-sm text-[#A0A0A5] font-body mt-2 flex items-center gap-2">
                  <MapPin size={14} className="text-[#C9A227]" />
                  {nextStop.passenger_point_address}
                  <span className="text-[10px] uppercase tracking-wider text-[#C9A227]">({nextStop.passenger_point_role})</span>
                </p>
                {(nextStop.flight_airline || nextStop.flight_number) && (
                  <p className="text-sm text-[#A0A0A5] font-body mt-1 flex items-center gap-2">
                    <Plane size={14} className="text-[#C9A227]" /> {[nextStop.flight_airline, nextStop.flight_number].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <a href={`tel:${nextStop.contact_phone}`} className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 uppercase tracking-wider font-display font-semibold text-xs rounded-lg" data-testid="call-client">
                  <Phone size={14} /> Llamar
                </a>
                <a href={`https://wa.me/${phoneDigits(nextStop.contact_phone)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 uppercase tracking-wider font-display font-semibold text-xs rounded-lg" data-testid="whatsapp-client">
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <Link to="/conductor/ruta" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C9A227] hover:bg-[#D4AF37] text-[#0D0E10] uppercase tracking-wider font-display font-bold text-xs rounded-lg" data-testid="navigate-btn">
                  Navegar <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Today's trips */}
        <div className="mt-8">
          <h2 className="font-display font-bold text-lg uppercase tracking-wide mb-4">Viajes de hoy</h2>
          {!loading && todayTrips.length === 0 && (
            <p className="text-xs text-[#A0A0A5] font-body" data-testid="no-today-trips">No tienes viajes agendados para hoy.</p>
          )}
          <div className="grid grid-cols-1 gap-3">
            {todayTrips.map((r) => (
              <div key={r.id} className="bg-[#1C1D20] border border-[#2E2F33] hover:border-[#C9A227]/50 rounded-xl p-5 flex items-center gap-4 transition-all" data-testid={`today-trip-${r.id}`}>
                <div className="w-16 shrink-0 text-center">
                  <p className="font-display font-bold text-[#C9A227]">{timeLabel(r.scheduled_at)}</p>
                </div>
                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="min-w-0">
                    <p className="font-display font-semibold text-sm">{r.contact_name}</p>
                    <p className="text-xs text-[#A0A0A5] truncate">{r.passenger_point_address}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-[#A0A0A5]">Modalidad</p>
                    <p className="text-xs text-[#F5F5F5] font-body">
                      {r.modality}{r.seat_numbers.length > 0 ? ` · Asientos ${r.seat_numbers.join(", ")}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={STATUS_LABELS[r.status]} />
                    <span className="font-display font-bold text-[#C9A227] text-sm">S/ {r.price_total}</span>
                  </div>
                </div>
                <ChevronRight size={16} className="text-[#A0A0A5]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </PanelShell>
  );
};

const MiniStat = ({ label, value, unit, gold }) => (
  <div className="bg-[#1C1D20] border border-[#2E2F33] rounded-xl p-5">
    <p className="text-[10px] uppercase tracking-brand text-[#A0A0A5] font-display">{label}</p>
    <p className={`font-display font-bold text-2xl mt-2 ${gold ? "text-[#C9A227]" : "text-[#F5F5F5]"}`}>{value}</p>
    <p className="text-[10px] text-[#A0A0A5] font-body mt-1">{unit}</p>
  </div>
);

export default DriverDashboard;
